import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { TranslationContext } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { AttributeType, PreferenceOption, SearchTermType } from "@is/types";

type Props = {
  preferences: PreferenceOption[];
  onPreferenceClick: (preference: PreferenceOption) => void;
  areOptionsExpanded: boolean;
  setAreOptionsExpanded: (value: boolean) => void;
  tabOptions: PreferenceOption[];
  optionsSelectedFromTab: PreferenceOption[];
};

const useManageAttributeOptions = ({
  preferences,
  onPreferenceClick,
  areOptionsExpanded,
  setAreOptionsExpanded,
  tabOptions,
  optionsSelectedFromTab,
}: Props) => {
  const { t } = useTranslation(TranslationContext.ProductListing);

  const { selectedTab, searchTerms } = useContext(ViewStateContext).content;

  const lengthOptions = useMemo(
    () =>
      preferences?.find(
        (preference) => preference?.key === AttributeType.Length
      )?.options,
    [preferences]
  );

  const showOther = useMemo(
    () =>
      !areOptionsExpanded &&
      optionsSelectedFromTab?.length < tabOptions?.length,
    [areOptionsExpanded, optionsSelectedFromTab, tabOptions]
  );

  const handleOptionSelect = useCallback(
    (option: PreferenceOption) => {
      onPreferenceClick(option);
    },
    [onPreferenceClick]
  );

  const subCategories = useMemo(() => {
    const options = optionsSelectedFromTab?.reduce(
      (previous: PreferenceOption[], current: PreferenceOption) => {
        if (current?.options?.length) {
          previous = [...previous, ...current.options.map((option) => option)];
        }

        return previous;
      },
      []
    );

    return options?.filter(
      (option) => option.type === SearchTermType.Subcategory
    );
  }, [optionsSelectedFromTab]);

  const hasSelectedSizes = useMemo(
    () =>
      searchTerms?.some(
        (searchTerm) => searchTerm.type === SearchTermType.Size
      ),
    [searchTerms]
  );

  const showLength = useMemo(
    () => selectedTab === AttributeType.Sizes && !!lengthOptions?.length,
    [lengthOptions, selectedTab]
  );

  const allOptions = useMemo(
    () => (areOptionsExpanded ? tabOptions : optionsSelectedFromTab),
    [areOptionsExpanded, optionsSelectedFromTab, tabOptions]
  );

  const handleExpandOptions = useCallback(() => {
    setAreOptionsExpanded(true);
  }, [setAreOptionsExpanded]);

  return {
    allOptions,
    handleOptionSelect,
    handleExpandOptions,
    showLength,
    hasSelectedSizes,
    lengthOptions,
    subCategories,
    showOther,
    selectedTab,
    t,
  };
};

export default useManageAttributeOptions;
